/**
 * Theme preference persistence and application. The inline script in
 * index.html mirrors `resolveInitialDark` so the first paint uses the right
 * theme; keep the storage key and logic in sync with it.
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'resolve-theme';
export const DARK_QUERY = '(prefers-color-scheme: dark)';

export const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'system' || value === 'light' || value === 'dark';

/** Stored preference, or `system` when absent, invalid, or unreadable. */
export const readStoredTheme = (): ThemePreference => {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(value) ? value : 'system';
  } catch {
    return 'system';
  }
};

export const storeTheme = (theme: ThemePreference): void => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage can be unavailable (private mode, quota); the choice then
    // lasts for this page view only.
  }
};

export const systemPrefersDark = (): boolean => {
  try {
    return window.matchMedia(DARK_QUERY).matches;
  } catch {
    return false;
  }
};

export const resolveTheme = (
  theme: ThemePreference,
  systemDark: boolean
): ResolvedTheme =>
  theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

/** Toggle the `dark` class and point theme-color metas at the surface token. */
export const applyTheme = (resolved: ResolvedTheme): void => {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  const surface = getComputedStyle(root)
    .getPropertyValue('--color-surface')
    .trim();
  if (!surface) return;
  document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((meta) => {
      meta.content = `rgb(${surface})`;
    });
};
